import { createContext, useContext } from 'react';

// Where the content pages are mounted: the SPA shell (default) or the legacy /admin/programs page
// (`admin/ProgramsPage`). Links into SPA-only pages (the program lessons page) need a legacy
// destination on that mount.
export type ContentMount = 'legacy' | 'spa';

const ContentMountContext = createContext<ContentMount>('spa');

export const ContentMountProvider = ContentMountContext.Provider;

export const useContentMount = () => useContext(ContentMountContext);

// The program lessons page (table of contents) on each mount. The legacy mount is based at
// /admin/programs and routes /:programId/lessons, the Rails lessons index URL, so a reload lands
// on the same page.
export const programLessonsPath = (programId: number, mount: ContentMount) => (
  mount === 'legacy' ? `/${programId}/lessons` : `/content/programs/${programId}`
);

// The sections page, the lesson's home, on each mount; the legacy mount routes it under the
// program (the Rails /admin/lessons/:id/sections URL redirects here behind the flag).
export const lessonSectionsPath = (programId: number, lessonId: number, mount: ContentMount) => (
  mount === 'legacy'
    ? `/${programId}/lessons/${lessonId}/sections`
    : `/content/programs/${programId}/lessons/${lessonId}/sections`
);

// The print tools page on each mount, the SPA home of the legacy sections page's Print Tools tab.
export const lessonPrintToolsPath = (programId: number, lessonId: number, mount: ContentMount) => (
  mount === 'legacy'
    ? `/${programId}/lessons/${lessonId}/print-tools`
    : `/content/programs/${programId}/lessons/${lessonId}/print-tools`
);

// The lesson guides page on each mount; the legacy mount routes it under the program (the Rails
// /admin/lessons/:id/slide_shows URL redirects here behind the flag).
export const lessonGuidesPath = (programId: number, lessonId: number, mount: ContentMount) => (
  mount === 'legacy'
    ? `/${programId}/lessons/${lessonId}/guides`
    : `/content/programs/${programId}/lessons/${lessonId}/guides`
);

// The unit slideshows page on each mount: the unit-level counterpart of lesson guides.
export const unitSlideShowsPath = (programId: number, unitId: number, mount: ContentMount) => (
  mount === 'legacy'
    ? `/${programId}/units/${unitId}/guides`
    : `/content/programs/${programId}/units/${unitId}/guides`
);

// The edit unit page on each mount; the legacy mount routes it under the program like the Rails
// units URLs (the Rails edit URL, /admin/units/:id/edit, redirects here behind the flag).
export const unitEditPath = (programId: number, unitId: number, mount: ContentMount, section = '') => {
  const base = mount === 'legacy'
    ? `/${programId}/units/${unitId}/edit`
    : `/content/programs/${programId}/units/${unitId}/edit`;

  return section ? `${base}/${section}` : base;
};
